import { useIntl } from "react-intl"
import { daysBetween } from "@/components/flashcards/dates"

const DAY_ONLY = { timeZone: "UTC" } as const

/** Parses a YYYY-MM-DD string as a UTC date, so the day never shifts with the viewer's time zone. */
export function parseIsoDate(iso: string) {
  const [y, m, d] = iso.split("-").map(Number)
  return new Date(Date.UTC(y, m - 1, d))
}

/**
 * Date, number and relative-time formatting in the active locale. Use this
 * instead of toLocaleString("en-US") or hand-built "n days" strings.
 */
export function useFormat() {
  const intl = useIntl()
  return {
    locale: intl.locale,
    /** A calendar date (YYYY-MM-DD) such as an exam date; no time zone shift. */
    date: (iso: string, options: Intl.DateTimeFormatOptions = { month: "long", day: "numeric", year: "numeric" }) =>
      intl.formatDate(parseIsoDate(iso), { ...options, ...DAY_ONLY }),
    /** A moment in time (ISO timestamp), shown in the viewer's time zone. */
    dateTime: (iso: string, options: Intl.DateTimeFormatOptions = { dateStyle: "medium", timeStyle: "short" }) =>
      intl.formatDate(new Date(iso), options),
    number: (n: number, options?: Intl.NumberFormatOptions) => intl.formatNumber(n, options),
    /** "in 3 days", "tomorrow" (numeric: "auto"), and so on, from a count of days. */
    relativeDays: (days: number) => intl.formatRelativeTime(days, "day", { numeric: "auto" }),
    /** "today", "tomorrow", "in 3 days", or "on Nov 12", relative to `todayIso` (both YYYY-MM-DD). */
    relativeDay: (dateIso: string, todayIso: string) => {
      const days = daysBetween(todayIso, dateIso)
      if (days < 7) return intl.formatRelativeTime(Math.max(days, 0), "day", { numeric: "auto" })
      const date = intl.formatDate(parseIsoDate(dateIso), { month: "short", day: "numeric", ...DAY_ONLY })
      return intl.formatMessage({ id: "flashcards.on_date" }, { date })
    },
    /** Compact interval for rating buttons: "today", "3d", "2mo", "1.5y". */
    interval: (days: number) => {
      if (days <= 0) return intl.formatMessage({ id: "flashcards.today" })
      if (days < 30) return intl.formatMessage({ id: "flashcards.interval_days" }, { count: days })
      if (days < 365) return intl.formatMessage({ id: "flashcards.interval_months" }, { count: Math.round(days / 30) })
      return intl.formatMessage({ id: "flashcards.interval_years" }, { years: intl.formatNumber(days / 365, { maximumFractionDigits: 1, minimumFractionDigits: 1 }) })
    },
    /** "3 cases" */
    caseCount: (count: number) => intl.formatMessage({ id: "exams.case_count" }, { count }),
    /** "3 questions" */
    questionCount: (count: number) => intl.formatMessage({ id: "quizzes.question_count" }, { count }),
    /** "Best 80%" or "Not taken yet" */
    bestScore: (score: number | null | undefined) =>
      score === null || score === undefined
        ? intl.formatMessage({ id: "quizzes.not_taken" })
        : intl.formatMessage({ id: "quizzes.best_score" }, { score }),
    /** "1,200 words" */
    wordCount: (count: number) => intl.formatMessage({ id: "sources.word_count" }, { count }),
    /** "3 cards" */
    cardCount: (count: number) => intl.formatMessage({ id: "flashcards.card_count" }, { count }),
    /** "2 hr 5 min" or "45 min" for time used or remaining. */
    duration: (seconds: number) => {
      const minutes = Math.round(seconds / 60)
      const hours = Math.floor(minutes / 60)
      const rest = minutes % 60
      if (hours === 0) return intl.formatMessage({ id: "format.minutes" }, { count: rest })
      if (rest === 0) return intl.formatMessage({ id: "format.hours" }, { count: hours })
      return intl.formatMessage({ id: "format.hours_minutes" }, { hours, minutes: rest })
    },
  }
}
